import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../api';
import {
  GetSalesPlanResponse,
  SalesChannel,
  SalesPlan as SalesPlanType,
} from '@dreamai/types';
import { Link } from 'react-router-dom';
import ThreadViewContainer from './chat/sidebar/ThreadViewContainer';
import { AgentProvider } from '../contexts/AgentContext';
import { ThreadLoadingProvider } from '../contexts/ThreadLoadingContext';
import './sales-plan/sales-plan.scss';

interface ChannelCardProps {
  channel: SalesChannel;
  type: 'ai' | 'user' | 'parked';
}

const ChannelCard: React.FC<ChannelCardProps> = ({ channel, type }) => {
  const getIcon = () => {
    const name = channel.channelName.toLowerCase();
    if (name.includes('email') || name.includes('outbound')) return '📧';
    if (name.includes('phone') || name.includes('call')) return '📞';
    if (name.includes('conference') || name.includes('speaking')) return '🎤';
    if (name.includes('linkedin')) return '💼';
    if (name.includes('partner')) return '🤝';
    if (name.includes('content')) return '📝';
    return '📊';
  };

  const getStatusBadge = () => {
    if (type === 'parked') {
      return <span className='status-badge parked'>Parked</span>;
    }
    if (type === 'ai') {
      return <span className='status-badge ai-managed'>AI-managed</span>;
    }
    return <span className='status-badge user-managed'>You manage</span>;
  };

  return (
    <div className={`channel-card ${type}`}>
      <div className='channel-header'>
        <span className='channel-icon'>{getIcon()}</span>
        <h3 className='channel-name'>{channel.channelName}</h3>
        {getStatusBadge()}
      </div>
      {channel.description && (
        <p className='channel-description'>{channel.description}</p>
      )}
      {channel.currentState && (
        <div className='channel-state'>
          <strong>Current state:</strong> {channel.currentState}
        </div>
      )}
      {channel.nextSteps && (
        <div className='channel-next-steps'>
          <strong>Next steps:</strong> {channel.nextSteps}
        </div>
      )}
      {type === 'ai' && (
        <div className='channel-link'>
          <Link to='/campaigns'>View campaigns →</Link>
        </div>
      )}
      {type === 'user' && (
        <div className='channel-link'>
          <span className='info-text'>Tasks appear in Dream Daily</span>
        </div>
      )}
    </div>
  );
};

const SalesPlan: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<GetSalesPlanResponse | null>(null);

  useEffect(() => {
    const fetchSalesPlan = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<GetSalesPlanResponse>('/sales-plan', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setData(response.data);
      } catch (err) {
        console.error('Error fetching sales plan:', err);
        setError('Failed to load sales plan');
      } finally {
        setLoading(false);
      }
    };

    fetchSalesPlan();
  }, [getAccessTokenSilently]);

  if (loading) {
    return (
      <div className='sales-plan-page'>
        <div className='loading'>Loading sales plan...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='sales-plan-page'>
        <div className='error'>{error}</div>
      </div>
    );
  }

  if (!data?.hasPlan) {
    return (
      <div className='sales-plan-page'>
        <div className='no-plan'>
          <h2>No Sales Plan Yet</h2>
          <p>
            Your AI sales assistant is still working on your sales plan. Check
            back soon or chat with your sales assistant to get started.
          </p>
        </div>
      </div>
    );
  }

  const { plan, channels } = data;
  const hasActiveChannels =
    channels.aiManaged.length > 0 || channels.userManaged.length > 0;

  return (
    <div className='sales-plan-page'>
      <header className='plan-header'>
        <h1>Your Sales Plan</h1>
        {plan?.updatedAt && (
          <span className='last-updated'>
            Last updated:{' '}
            {new Date(plan.updatedAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
        )}
      </header>

      {plan?.generalNotes && (
        <section className='plan-section strategy-summary'>
          <h2>The Thinking</h2>
          <div className='summary-content'>{plan.generalNotes}</div>
        </section>
      )}

      {hasActiveChannels && (
        <>
          <section className='plan-section channel-overview'>
            <h2>Your Channel Mix</h2>
            <div className='channel-cards-row'>
              {channels.aiManaged.map((channel) => (
                <div key={channel.id} className='channel-card-mini ai'>
                  <span className='mini-icon'>
                    {channel.channelName.toLowerCase().includes('email')
                      ? '📧'
                      : '📊'}
                  </span>
                  <span className='mini-name'>{channel.channelName}</span>
                  <span className='mini-status'>AI-managed</span>
                </div>
              ))}
              {channels.userManaged.map((channel) => (
                <div key={channel.id} className='channel-card-mini user'>
                  <span className='mini-icon'>🎯</span>
                  <span className='mini-name'>{channel.channelName}</span>
                  <span className='mini-status'>You manage</span>
                </div>
              ))}
              {channels.parked.map((channel) => (
                <div key={channel.id} className='channel-card-mini parked'>
                  <span className='mini-icon'>⏸️</span>
                  <span className='mini-name'>{channel.channelName}</span>
                  <span className='mini-status'>Parked</span>
                </div>
              ))}
            </div>
          </section>

          {channels.aiManaged.length > 0 && (
            <section className='plan-section'>
              <h2>AI-Managed Channels</h2>
              <p className='section-description'>
                Dream handles these channels for you automatically.
              </p>
              <div className='channel-cards'>
                {channels.aiManaged.map((channel) => (
                  <ChannelCard key={channel.id} channel={channel} type='ai' />
                ))}
              </div>
            </section>
          )}

          {channels.userManaged.length > 0 && (
            <section className='plan-section'>
              <h2>Channels You Manage</h2>
              <p className='section-description'>
                Tasks for these channels will appear in your Dream Daily list.
              </p>
              <div className='channel-cards'>
                {channels.userManaged.map((channel) => (
                  <ChannelCard key={channel.id} channel={channel} type='user' />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {channels.parked.length > 0 && (
        <section className='plan-section parked-section'>
          <h2>Parked Channels</h2>
          <p className='section-description'>
            Not pursuing these yet - focusing on fewer channels first.
          </p>
          <div className='channel-cards'>
            {channels.parked.map((channel) => (
              <ChannelCard key={channel.id} channel={channel} type='parked' />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default SalesPlan;
